import { get, map } from "lodash";
import { createContext, Fragment, ReactChild, ReactFragment, ReactPortal, useContext, useEffect, useState } from "react";
import { getProgressColorCode } from "@pages/utils/utils";
import { Button, Spin } from "antd";
import "./utilizationReview.scss";
import Logo from "@assets/logo-compressed.png";
import MyCareLogo from "@assets/images/myCareLogo.png";
import DemoLogo from "@assets/images/dashboardReport.png";
import envelop_png from "@assets/images/envelop_png.png";
import internet_png from "@assets/images/internet_png.png";
import Icon from "@assets/icon_config";
import memberDetailsService from "@services/memberDetails.service";
import membersService from "@services/members.service";
import { formatDisplayDate } from "@utils/helpers";
import { usePdfContext } from "../pdf";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";

const useUtilizationReport = (props: any) => {
  const { setisOpen } = usePdfContext();

  const [utilizationReportData, setUtilizationReportData] = useState<any>({});
  const [isModalLoading, setIsModalLoading] = useState(false);

  const getUtilizationReportData = (patientId: string) => {
    setIsModalLoading(true);
    setUtilizationReportData({});
    memberDetailsService.fetchUtilizationReviewReportInfo(patientId).subscribe({
      next: (v) => {
        const response = v.data.data;
        setUtilizationReportData(response);
        setIsModalLoading(false);
        setisOpen && setisOpen(true);
      },
      error: (e) => {
        console.log(e);
        setIsModalLoading(false);
        setisOpen && setisOpen(false);
      },
    });
  };

  const onPdfOpen = () => {
    getUtilizationReportData(membersService?.getCurrentMember?.patient_id);
  };

  return {
    utilizationReportData,
    isModalLoading,
    onPdfOpen
  };
};

const UtilizationReportContext = createContext<any>({});

export const UtilizationReportProvider = ({ children, ...props }: any) => {
  const utilizationReport = useUtilizationReport(props);
  return <UtilizationReportContext.Provider value={utilizationReport}>{children}</UtilizationReportContext.Provider>;
};

export const useUtilizationReportContext = () => useContext(UtilizationReportContext);

export const PdfOpenButon = (props: { title: string; reviewReason?: any }) => {
  const { onPdfOpen, isModalLoading } = useUtilizationReportContext();
  useEffect(() => {
    if (props.reviewReason) {
      onPdfOpen && onPdfOpen();
    }
  }, [props.reviewReason]);

  return (
    <Spin spinning={isModalLoading} indicator={<HealthemSpinner />}>
      <Button
        type="default"
        color="black"
        className="pdf-action-button"
        onClick={(e) => {
          e.stopPropagation();
          onPdfOpen && onPdfOpen();
        }}
      >
        <Icon name="pdf" />
        {props.title}
      </Button>
    </Spin>
  );
};

const UtilizationReport = (props: any) => {
  const { utilizationReportData, isModalLoading } = useUtilizationReportContext();

  let headerLogo = MyCareLogo;

  if (process.env.REACT_APP_ENVIRONMENT === "demo") {
    headerLogo = DemoLogo;
  }

  const valueFormatter: any = {
    DOB: (value: string) => {
      const dobAge = value.split('#AGE#')
      return (dobAge[0] !== "--" ? formatDisplayDate(dobAge[0], "YYYY-MM-DD") : dobAge[0]) + " " + dobAge[1];
    },
    "Insurance Effective Date": (value: string) => (value !== "--" ? formatDisplayDate(value, "YYYY-MM-DD") : value),
    "LOV (Last Assigned Office Visit)": (value: string) => (value !== "--" ? formatDisplayDate(value, "YYYY-MM-DD") : value),
    "NOV (Next Assigned Office Visit)": (value: string) => (value !== "--" ? formatDisplayDate(value, "YYYY-MM-DD") : value),
  };

  return (
    <Spin spinning={isModalLoading} indicator={<HealthemSpinner size={70}/>}>
      <div className="ui-utilization-review">
        {/* Header */}
        <div className="ui-utilization-review__patient-info">
          <img src={headerLogo} alt="company logo" className="ui-utilization-review__patient-info__logo" />
          <div className="ui-utilization-review__patient-info__content">
            {utilizationReportData?.patientInfo?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
              return (
                <Fragment key={item.label}>
                  <div className="ui-utilization-review__patient-info__content__data__label">{item.label}</div>
                  <div className={`ui-utilization-review__patient-info__content__data__value ${item.label.toLowerCase().replaceAll(" ", "-")}`}>{item.value}</div>
                </Fragment>
              );
            })}
          </div>
        </div>
        {/* Division 1 */}
        <hr className="ui-utilization-review__division ui-utilization-review__division--top"></hr>
        <div className="ui-utilization-review__title">
          <div className="ui-utilization-review__title__text">Utilization Review Report </div>
          <span className="date">{formatDisplayDate(new Date().toISOString(), "YYYY-MM-DDTHH:MM:SS")}</span>
        </div>
        <div className="ui-utilization-review__report-review">
          <div className="ui-utilization-review__report-review__left">
            {utilizationReportData?.reportReview?.slice(0, 8)?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
              let value = valueFormatter[item.label] ? valueFormatter[item.label](item.value) : item.value;
              return (
                <div key={item.label} className="ui-utilization-review__report-review__left__data">
                  <div className="ui-utilization-review__report-review__left__data__label">{item.label}</div>
                  <div className="ui-utilization-review__report-review__left__data__value">{value}</div>
                </div>
              );
            })}
          </div>
          <div className="ui-utilization-review__report-review__right">
            {utilizationReportData?.reportReview
              ?.slice(8, utilizationReportData?.reportReview.length)
              ?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
                let value = valueFormatter[item.label] ? valueFormatter[item.label](item.value) : item.value;
                return (
                  <div key={item.label} className="ui-utilization-review__report-review__right__data">
                    <div className="ui-utilization-review__report-review__right__data__label">{item.label}</div>
                    <div className="ui-utilization-review__report-review__right__data__value">{value}</div>
                  </div>
                );
              })}
          </div>
        </div>
        {/* Division 1.2 */}
        <div className="ui-utilization-review__report-review-part-one-a">
          <div className="ui-utilization-review__report-review-part-one-a__label">{utilizationReportData?.catastrophicConditions?.label}</div>
          <div className="ui-utilization-review__report-review-part-one-a__value">{utilizationReportData?.catastrophicConditions?.value}</div>
        </div>
        {/* Division 2 */}
        <hr className="ui-utilization-review__division"></hr>
        <div className="ui-utilization-review__report-review-part-two">
          <div className="ui-utilization-review__report-review-part-two__table">
            <div className="ui-utilization-review__report-review-part-two__table__left">
              {utilizationReportData?.reportReviewPartTwo?.slice(0, 2)?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
                let value = valueFormatter[item.label] ? valueFormatter[item.label](item.value) : item.value;
                return (
                  <div key={item.label} className="ui-utilization-review__report-review-part-two__table__left__data">
                    <div className="ui-utilization-review__report-review-part-two__table__left__data__label">{item.label}</div>
                    <div className="ui-utilization-review__report-review-part-two__table__left__data__value">{value}</div>
                  </div>
                );
              })}
            </div>
            <div className="ui-utilization-review__report-review-part-two__table__right">
              {utilizationReportData?.reportReviewPartTwo?.slice(2, 5)?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
                return (
                  <div key={item.label} className="ui-utilization-review__report-review-part-two__table__right__data">
                    <div className="ui-utilization-review__report-review-part-two__table__right__data__label">{item.label}</div>
                    <div className="ui-utilization-review__report-review-part-two__table__right__data__value">{item.value}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="ui-utilization-review__report-review-part-two__list">
            <h3> {utilizationReportData?.reportReviewPartTwo?.[5]?.label}</h3>
            {utilizationReportData?.reportReviewPartTwo?.[5]?.value?.map((item: string, index: number) => {
              return (
                <div key={index} className="ui-utilization-review__report-review-part-two__list__value">
                  {item}{" "}
                </div>
              );
            })}
          </div>
        </div>
        {/* Division 3 */}
        <hr className="ui-utilization-review__division"></hr>
        <div className="ui-utilization-review__report-review-part-three">
          <div className="ui-utilization-review__report-review-part-three__table">
            <TableView
              dataSource={utilizationReportData?.reportReviewPartThree?.sectionLeft}
              columnKeyCombo={[
                {
                  column: "Total Utilization",
                  key: "totalUtilization",
                },
                {
                  column: "Last Month",
                  key: "lastMonth",
                },
                {
                  column: "Last 12 Months",
                  key: "last12Month",
                },
              ]}
            />
          </div>
          <div className="ui-utilization-review__division-vertical"></div>
          <div className="ui-utilization-review__report-review-part-three__percentage">
            {utilizationReportData?.reportReviewPartThree?.sectionRight?.map((item: { label: string; value: number | undefined; color: string | undefined }) => {
              return (
                <div key={item.label} className="ui-utilization-review__report-review-part-three__percentage__block">
                  <div className="ui-utilization-review__report-review-part-three__percentage__block__data">
                    <div className="ui-utilization-review__report-review-part-three__percentage__block__data__label"> {item?.label}</div>
                    <div className="ui-utilization-review__report-review-part-three__percentage__block__data__value">{item?.value}%</div>
                  </div>
                  <div className="progress">
                    <div className="progress-track">
                      <div
                        className="progress-rail"
                        style={{
                          width: `${item?.value || 0}%`,
                          backgroundColor: getProgressColorCode(item?.color),
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Division 4 */}
        <hr className="ui-utilization-review__division"></hr>
        <div className="ui-utilization-review__report-review-part-four">
          <TableView
            dataSource={[utilizationReportData?.reportReviewPartFour?.data]}
            columnKeyCombo={[
              {
                column: utilizationReportData?.reportReviewPartFour?.title,
                key: undefined,
              },
              {
                column: "Last Month",
                key: "lastMonth",
              },
              {
                column: "Last 12 Month",
                key: "last12Month",
              },
            ]}
          />
        </div>
        {/* Division 5 */}
        <hr className="ui-utilization-review__division"></hr>
        <div className="ui-utilization-review__report-review-part-five">
          <TableView
            dataSource={utilizationReportData?.reportReviewPartFive?.sectionLeft}
            columnKeyCombo={[
              {
                column: "Total Spend (Claims only)",
                key: "totalCosts",
              },
              {
                column: "Last Month",
                key: "lastMonth",
              },
              {
                column: "Last 12 Months",
                key: "last12Month",
              },
            ]}
          />
          <div className="ui-utilization-review__division-vertical"></div>
          <TableView
            dataSource={utilizationReportData?.reportReviewPartFive?.sectionRight}
            columnKeyCombo={[
              {
                column: "Total Spend (Claims only)",
                key: "totalCosts",
              },
              {
                column: "Last Month",
                key: "lastMonth",
              },
              {
                column: "Last 12 Months",
                key: "last12Month",
              },
            ]}
          />
        </div>
        {/* Footer */}
        <hr className="ui-utilization-review__division"></hr>
        <div className="ui-utilization-review__footer">
          <img src={Logo} alt="company logo" className="ui-utilization-review__footer__logo" />
          <div className="ui-utilization-review__footer__content">
            <div className="ui-utilization-review__footer__content__data">
              <div className="ui-utilization-review__footer__content__data__icon">
                {/* <Icon name={"envelope"} /> */}
                <img src={envelop_png} alt="company logo" />
              </div>
              <div className="ui-utilization-review__footer__content__data__value">support.healthem@tredence.com</div>
            </div>
            <div className="ui-utilization-review__footer__content__data">
              <div className="ui-utilization-review__footer__content__data__icon">
                {/* <Icon name={"internet"} colorOfBrand="#d15f25" /> */}
                <img src={internet_png} alt="company logo" />
              </div>
              <div className="ui-utilization-review__footer__content__data__value">https://www.healthem.ai</div>
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

const TableView = (props: { dataSource: any; columnKeyCombo: any[] }) => {
  const dataSource = props?.dataSource;

  return (
    <table className="ui-utilization-review__table-view">
      <thead>
        <tr className="ui-utilization-review__table-view__head">
          {props.columnKeyCombo?.map((item, index) => {
            return <th key={item.column + index}>{item.column}</th>;
          })}
        </tr>
      </thead>
      <tbody className="ui-utilization-review__table-view__body">
        {dataSource?.map(
          (item: {
            totalUtilization: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined;
            lastMonth: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined;
            last12Month: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined;
            metaData: any;
          }) => {
            return (
              <>
                <tr className="ui-utilization-review__table-view__body__row">
                  {props.columnKeyCombo?.map((_item, index) => {
                    return index === 0 ? (
                      <td key={get(item, _item.key, index) + index} className="ui-utilization-review__table-view__body__row__label">
                        {get(item, _item.key, "")}
                      </td>
                    ) : (
                      <td key={get(item, _item.key, index) + index} className="ui-utilization-review__table-view__body__row__value">
                        {get(item, _item.key, "")}
                      </td>
                    );
                  })}
                </tr>

                {/* if there is any meta data present for the row, then draw this as extra info */}
                {item?.metaData
                  ? map(item?.metaData, (infoData) => {
                      return (
                        <tr className="ui-utilization-review__table-view__body__row">
                          {props.columnKeyCombo?.map((_item, index) => {
                            return index === 0 ? (
                              <td
                                key={get(infoData, _item.key, index) + index}
                                className="ui-utilization-review__table-view__body__row__label ui-utilization-review__table-view__body__row__label--for-meta-data"
                              >
                                {get(infoData, _item.key, "")}
                              </td>
                            ) : (
                              <td key={get(infoData, _item.key, index) + index} className="ui-utilization-review__table-view__body__row__value">
                                {get(infoData, _item.key, "")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  : null}
              </>
            );
          }
        )}
      </tbody>
    </table>
  );
};

export default UtilizationReport;
